import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import Diversity3OutlinedIcon from '@mui/icons-material/Diversity3Outlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import { headerGradient } from '../../theme';
import { fetchPublicContentModule } from '../../services/publicContentApi';
import type { SecadDocumentItem } from '../../types/content';

const fallbackDocuments: SecadDocumentItem[] = [
  {
    id: 'monitoria-manual',
    title: 'Manual do Programa de Monitoria Acadêmica',
    href: '/assets/docs/monitoria/manual-programa-monitoria-academica.pdf',
    order: 1,
    isActive: true,
    resourceType: 'file',
  },
  {
    id: 'monitoria-edital-atual',
    title: 'Edital Monitoria 2026.2',
    href: '/assets/docs/monitoria/edital-monitoria-2026-2.pdf',
    order: 2,
    isActive: true,
    resourceType: 'file',
  },
];
const INSCRICAO_2026_URL = 'https://forms.cloud.microsoft/r/z4LKsbz6sQ';
const INSCRICAO_2025_URL = 'https://forms.cloud.microsoft/r/9cfnZqGqk3';

const atividades = [
  { icon: AutoStoriesOutlinedIcon, title: 'Aprofunde seus conhecimentos', description: 'Amplie conhecimentos já adquiridos e fortaleça sua formação acadêmica.' },
  { icon: TipsAndUpdatesOutlinedIcon, title: 'Desenvolva habilidades', description: 'Pratique comunicação, autonomia, responsabilidade, organização e trabalho colaborativo.' },
  { icon: Diversity3OutlinedIcon, title: 'Compartilhe conhecimentos', description: 'Contribua para a aprendizagem de outros estudantes e para uma vivência mais colaborativa.' },
  { icon: SchoolOutlinedIcon, title: 'Vivencie a experiência acadêmica', description: 'Aproxime-se do apoio ao ensino e das práticas acadêmicas e didático-pedagógicas.' },
] as const;

const SectionTitle: React.FC<{ children: React.ReactNode; width?: number }> = ({ children, width = 118 }) => (
  <Typography
    component="h2"
    variant="h5"
    sx={{
      fontWeight: 700,
      color: 'primary.main',
      mb: 2.5,
      position: 'relative',
      pb: 1.1,
      '&::after': { content: '""', position: 'absolute', left: 0, bottom: 0, width, height: 3, bgcolor: 'secondary.main' },
    }}
  >
    {children}
  </Typography>
);

const Monitoria: React.FC = () => {
  const theme = useTheme();
  const [documents, setDocuments] = useState<SecadDocumentItem[]>(fallbackDocuments);

  useEffect(() => {
    void fetchPublicContentModule<SecadDocumentItem>('monitoringDocuments')
      .then((response) => {
        const activeItems = response.items.filter((item) => item.isActive);
        if (activeItems.length) setDocuments(activeItems);
      })
      .catch(() => {
        // Os documentos publicados no build continuam disponíveis caso a API esteja indisponível.
      });
  }, []);

  const documentLinks = useMemo(() => {
    const byId = new Map(documents.map((item) => [item.id, item]));
    return {
      manual: byId.get('monitoria-manual')?.href || fallbackDocuments[0].href,
      edital: byId.get('monitoria-edital-atual')?.href || fallbackDocuments[1].href,
    };
  }, [documents]);

  return (
    <Box
      sx={{
        overflow: 'hidden',
        bgcolor: '#fff',
        // Compensa o recuo padrão do conteúdo interno do site para que as faixas
        // visuais ocupem toda a largura disponível, sem bordas laterais vazias.
        width: { xs: 'calc(100% + 32px)', md: 'calc(100% + 64px)' },
        ml: { xs: -2, md: -4 },
      }}
    >
      <Box
        sx={{
          pt: { xs: 7, md: 10 },
          pb: { xs: 7, md: 9 },
          color: '#fff',
          textAlign: 'center',
          background: headerGradient,
        }}
      >
        <Container maxWidth="md">
          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mb: 2.5 }}>
            <Chip icon={<SchoolOutlinedIcon />} label="Cursos de graduação" sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: '#fff', '& .MuiChip-icon': { color: '#fff' } }} />
            <Chip icon={<Diversity3OutlinedIcon />} label="Experiência formativa" sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: '#fff', '& .MuiChip-icon': { color: '#fff' } }} />
          </Stack>
          <Typography component="h1" variant="h2" sx={{ fontWeight: 800, fontSize: { xs: '2.15rem', md: '3.25rem' }, lineHeight: 1.1 }}>
            Programa de Monitoria Acadêmica
          </Typography>
          <Typography sx={{ maxWidth: 720, mx: 'auto', mt: 2.25, fontSize: { xs: '1rem', md: '1.15rem' }, lineHeight: 1.75, opacity: 0.94 }}>
            Uma oportunidade para aprender mais, compartilhar saberes e viver a Faculdade de perto.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          <Grid item xs={12} md={8}>
            <SectionTitle width={88}>Sobre</SectionTitle>
            <Box sx={{ '& p': { color: 'text.secondary', textAlign: 'justify', lineHeight: 1.85, mb: 2 } }}>
              <Typography>
                O Programa de Monitoria Acadêmica é uma iniciativa institucional da Faculdade de Tecnologia Senac Amazonas voltada ao fortalecimento do processo de ensino e aprendizagem e à ampliação das experiências formativas dos estudantes dos cursos de graduação.
              </Typography>
              <Typography>
                A monitoria permite participar de atividades de apoio ao ensino, acompanhadas por professores, e aproxima estudantes, docentes e diferentes períodos do curso em uma experiência de cooperação e aprendizagem colaborativa.
              </Typography>
              <Typography>
                Ao vivenciar práticas acadêmicas e didático-pedagógicas, o monitor desenvolve autonomia, responsabilidade, comunicação, organização e capacidade de compartilhar conhecimentos, além de conhecer novas possibilidades para sua formação profissional.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={5} sx={{ height: '100%', color: '#fff', background: 'linear-gradient(150deg, #003a70 0%, #285a8f 100%)', borderRadius: 3 }}>
              <CardContent sx={{ p: { xs: 3, md: 3.5 }, '&:last-child': { pb: { xs: 3, md: 3.5 } } }}>
                <MenuBookOutlinedIcon sx={{ fontSize: 44, color: 'secondary.main', mb: 2 }} />
                <Typography variant="h5" fontWeight={700}>Manual do Programa</Typography>
                <Typography sx={{ mt: 1.25, mb: 3, lineHeight: 1.7, color: 'rgba(255,255,255,0.86)' }}>
                  Consulte as normas, requisitos, atribuições e demais orientações para participar da monitoria.
                </Typography>
                <Button component="a" href={documentLinks.manual} target="_blank" rel="noopener noreferrer" variant="contained" color="secondary" startIcon={<DownloadOutlinedIcon />} fullWidth>
                  Acessar manual
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box component="section" sx={{ mt: { xs: 7, md: 10 } }}>
          <SectionTitle width={205}>Por que ser monitor?</SectionTitle>
          <Grid container spacing={2.5}>
            {atividades.map((atividade) => {
              const Icon = atividade.icon;
              return (
                <Grid item xs={12} sm={6} md={3} key={atividade.title}>
                  <Card elevation={0} variant="outlined" sx={{ height: '100%', borderRadius: 3, borderColor: '#e2e8f0', transition: 'transform 180ms ease, box-shadow 180ms ease', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 14px 28px rgba(0,58,112,0.12)' } }}>
                    <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                      <Box sx={{ display: 'grid', placeItems: 'center', width: 50, height: 50, borderRadius: 2.5, color: 'primary.main', bgcolor: '#eef5fc', mb: 2 }}>
                        <Icon />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.35, mb: 1.25 }}>{atividade.title}</Typography>
                      <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>{atividade.description}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        <Box component="section" sx={{ mt: { xs: 7, md: 10 } }}>
          <SectionTitle width={190}>Processos seletivos</SectionTitle>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card elevation={4} sx={{ height: '100%', borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{ p: { xs: 3, md: 4 }, color: '#fff', background: headerGradient }}>
                  <Typography variant="overline" sx={{ letterSpacing: 1.2, fontWeight: 700, opacity: 0.9 }}>Seleção em andamento</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.25 }}>Programa 2026.2</Typography>
                  <Typography sx={{ mt: 1, opacity: 0.93 }}>Participe do processo seletivo para atuar como monitor em seu curso.</Typography>
                </Box>
                <CardContent sx={{ p: { xs: 3, md: 4 }, '&:last-child': { pb: { xs: 3, md: 4 } } }}>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={7}>
                      <Typography variant="caption" color="text.secondary">PERÍODO DE INSCRIÇÕES</Typography>
                      <Typography fontWeight={700} sx={{ mt: 0.25 }}>10/09/2026 a 17/09/2026</Typography>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <Typography variant="caption" color="text.secondary">RESULTADO</Typography>
                      <Typography fontWeight={700} sx={{ mt: 0.25 }}>Em breve</Typography>
                    </Grid>
                  </Grid>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button component="a" href={INSCRICAO_2026_URL} target="_blank" rel="noopener noreferrer" variant="contained" color="primary" endIcon={<ArrowOutwardIcon />}>
                      Inscreva-se agora
                    </Button>
                    <Button component="a" href={documentLinks.edital} target="_blank" rel="noopener noreferrer" variant="outlined" startIcon={<DownloadOutlinedIcon />}>
                      Ler edital completo
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ height: '100%', p: { xs: 3, md: 3.5 }, borderRadius: 3, borderColor: '#e2e8f0', bgcolor: '#f8fafc' }}>
                <Typography variant="overline" sx={{ color: 'secondary.main', fontWeight: 800, letterSpacing: 1.1 }}>Edição anterior</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.25 }}>Programa 2025.2</Typography>
                <Typography color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.7 }}>
                  Período de inscrições até 27/09/2025. Resultado: em breve.
                </Typography>
                <Button component="a" href={INSCRICAO_2025_URL} target="_blank" rel="noopener noreferrer" variant="text" endIcon={<ArrowOutwardIcon />} sx={{ mt: 2, px: 0 }}>
                  Edital e inscrições
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>

      <Box sx={{ color: '#fff', background: headerGradient, py: { xs: 5, md: 6 } }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems={{ md: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h5" fontWeight={700}>Ficou com alguma dúvida?</Typography>
              <Typography sx={{ mt: 0.75, opacity: 0.9 }}>A Coordenação do seu Curso e o NEPE podem ajudar.</Typography>
            </Box>
            <Button component="a" href="mailto:nepe.faculdade@am.senac.br" variant="contained" color="secondary" startIcon={<EmailOutlinedIcon />} sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}>
              Falar com o NEPE
            </Button>
          </Stack>
        </Container>
      </Box>

      <Box sx={{ py: 2.5, textAlign: 'center', bgcolor: theme.palette.background.default }}>
        <Typography variant="body2" color="text.secondary">
          Para mais informações, escreva para <Box component="a" href="mailto:nepe.faculdade@am.senac.br" sx={{ color: 'primary.main', fontWeight: 700 }}>nepe.faculdade@am.senac.br</Box>.
        </Typography>
      </Box>
    </Box>
  );
};

export default Monitoria;
